// /teams/:teamId/ai-models — AI Models panel per issue #22 acceptance.
//
// Two surfaces:
//
//   1. OpenRouter API key — V1 stub: Cairn-managed only. The "Bring your own
//      key" affordance is disabled with a tooltip pointing at #16, which
//      lands the GCP Secret Manager + client-side encryption pipe.
//
//   2. Per-task model overrides — four dropdowns (graphExtraction /
//      driftSummary / nlSearch / fingerprintVision). Each select offers
//      "Cairn default" as the sentinel + the OpenRouter catalog from the
//      proxy function. Selecting writes teams/{teamId}.openrouter
//      .modelOverrides.<task>. Worker reads this at job start (#22 worker
//      integration is a small follow-up once #18 produces real jobs).

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '../../components/dashboard/PageHeader.jsx'
import { Button } from '../../components/ui/button.jsx'
import { useTeam } from '../../lib/data-hooks.js'
import { KNOWN_TASKS, setModelOverride } from '../../lib/mutations.js'
import { useOpenRouterModels } from '../../lib/openrouter-models.js'

const TASK_LABELS = {
  graphExtraction: {
    title: 'Graph extraction',
    blurb: 'Voice transcript → flow YAML. Recording cinematic.',
    defaults: 'Claude Sonnet 4.5 · GPT-4o fallback',
  },
  driftSummary: {
    title: 'Drift summary',
    blurb: 'Renders the drift report into human-readable patches.',
    defaults: 'Claude Haiku 4.5 · GPT-4o-mini fallback',
  },
  nlSearch: {
    title: 'Natural-language search',
    blurb: 'Dashboard "find any flow that..." queries (V1.5).',
    defaults: 'GPT-4o-mini · Claude Haiku 4.5 fallback',
  },
  fingerprintVision: {
    title: 'Fingerprint vision',
    blurb: 'Multimodal element fingerprinting for drift detection.',
    defaults: 'GPT-4o · Claude Opus 4 fallback',
  },
}

function ModelRow({ task, currentOverride, models, onChange }) {
  const meta = TASK_LABELS[task]
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const value = currentOverride ?? '__cairn_default__'

  async function handleChange(e) {
    const choice = e.target.value
    setSubmitting(true)
    setError(null)
    try {
      await onChange(task, choice === '__cairn_default__' ? null : choice)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="grid gap-3 border-t border-[var(--color-sandstone)] py-5 sm:grid-cols-[1fr_auto] sm:items-center">
      <div>
        <h3 className="text-sm font-medium text-[var(--color-basalt)]">{meta.title}</h3>
        <p className="mt-0.5 text-xs text-[var(--color-schist)]">{meta.blurb}</p>
        <p className="mt-1 font-mono text-[11px] text-[var(--color-schist)]">
          Cairn default: {meta.defaults}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <select
          value={value}
          onChange={handleChange}
          disabled={submitting}
          className="min-w-[260px] rounded-md border border-[var(--color-sandstone)] bg-white px-3 py-1.5 text-sm focus:outline-none focus-visible:border-[var(--color-terracotta)]"
        >
          <option value="__cairn_default__">Cairn default (recommended)</option>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name ?? m.id}
            </option>
          ))}
        </select>
        {currentOverride && (
          <span className="rounded-sm bg-[var(--color-terracotta-soft)]/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-terracotta-deep)]">
            override
          </span>
        )}
        {error && (
          <span className="text-xs text-[var(--color-failed)]" role="alert">
            {error}
          </span>
        )}
      </div>
    </div>
  )
}

export function AiModels() {
  const { teamId } = useParams()
  const { data: team } = useTeam(teamId)
  const { data: models, loading: modelsLoading, error: modelsError } = useOpenRouterModels()

  const overrides = team?.openrouter?.modelOverrides ?? {}

  async function handleChange(task, modelId) {
    await setModelOverride({ teamId, task, modelId })
  }

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: 'Teams', to: '/teams' },
          { label: team?.name ?? teamId, to: `/teams/${teamId}` },
          { label: 'AI Models' },
        ]}
        title="AI Models"
        subhead="Bring your own OpenRouter key + override the default model per task."
      />

      <div className="mx-auto max-w-3xl px-6 py-8">
        {/* BYOK key section — gated on #16 GCP Secret Manager infra ------- */}
        <section className="mb-10 rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-medium text-[var(--color-basalt)]">
                OpenRouter API key
              </h2>
              <p className="mt-1 text-sm text-[var(--color-schist)]">
                The key Cairn uses to call OpenRouter on your behalf for graph extraction, drift
                summaries, and fingerprint vision.
              </p>
            </div>
            <span className="shrink-0 rounded-sm bg-[var(--color-sandstone)] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[var(--color-slate)]">
              Cairn-managed
            </span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              title="Client-side encryption + GCP Secret Manager wire lands with #16"
            >
              Bring your own key
            </Button>
            <span className="text-xs text-[var(--color-schist)]">
              Available when team secret storage lands{' '}
              <a
                href="https://github.com/cairntools/cairn/issues/16"
                className="text-[var(--color-terracotta)] hover:underline"
              >
                #16
              </a>
              .
            </span>
          </div>
        </section>

        {/* Per-task model overrides --------------------------------------- */}
        <section>
          <div className="mb-2 flex items-baseline justify-between">
            <h2 className="text-base font-medium text-[var(--color-basalt)]">
              Per-task model overrides
            </h2>
            {modelsLoading && (
              <span className="text-xs text-[var(--color-schist)]">Loading models…</span>
            )}
            {modelsError && (
              <span className="text-xs text-[var(--color-failed)]" role="alert">
                {modelsError.message}
              </span>
            )}
          </div>
          <p className="mb-4 text-sm text-[var(--color-schist)]">
            Cairn defaults are tuned for cost + latency. Override only when you have a reason — the
            LLM adapter still falls back to a second model if your pick is unreachable.
          </p>

          <div className="rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] px-5">
            {KNOWN_TASKS.map((task) => (
              <ModelRow
                key={task}
                task={task}
                currentOverride={overrides[task] ?? null}
                models={models}
                onChange={handleChange}
              />
            ))}
          </div>

          <p className="mt-4 text-xs text-[var(--color-schist)]">
            Overrides apply to every job in this team — including jobs queued by the CLI. Worker
            integration lands once #18 produces real voice-to-graph jobs.
          </p>
        </section>
      </div>
    </>
  )
}
